import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Save, Loader2, Plus, X, BookOpen, ChevronDown, Brain, Wand2, Users, AlertTriangle, TrendingUp, CheckCircle2 } from "lucide-react";
import api, { OBJECTIONS, NEXT_ACTIONS, getUser } from "@/lib/api";
import CustomFieldsSection from "@/components/CustomFieldsSection";
import useFieldsConfig from "@/lib/useFieldsConfig";
import { SAMPLE_CLINICAL_STATE_B1, SAMPLE_CLINICAL_STATE_B2, SAMPLE_CARE_PLAN, SAMPLE_DIAGNOSTIC_INTERVIEW } from "@/lib/sampleData";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const tabs = [
  { k: "clinical", t: "Entrevista Diagnóstico" },
  { k: "care_plan", t: "Plano de Cuidados" },
  { k: "activities", t: "Atividades" },
  { k: "final", t: "Configuração Final" },
  { k: "evaluation", t: "Avaliação & Fecho" },
];

const ACTIVITY_TEMPLATE = [
  { category: "cuidados_pessoais", activity: "Higiene / Banho" },
  { category: "cuidados_pessoais", activity: "Vestir" },
  { category: "cuidados_pessoais", activity: "Alimentação" },
  { category: "cuidados_pessoais", activity: "Hidratação" },
  { category: "cuidados_pessoais", activity: "Mobilidade / Transferências" },
  { category: "cuidados_pessoais", activity: "Administração de medicação" },
  { category: "cuidados_pessoais", activity: "Companhia / Estimulação cognitiva" },
  { category: "cuidados_pessoais", activity: "Passeios / Consultas" },
  { category: "manutencao_espaco", activity: "Limpeza do quarto" },
  { category: "manutencao_espaco", activity: "Limpeza da casa de banho" },
  { category: "manutencao_espaco", activity: "Limpeza da cozinha" },
  { category: "manutencao_espaco", activity: "Limpeza das áreas comuns" },
  { category: "manutencao_espaco", activity: "Organização dos espaços" },
  { category: "roupa", activity: "Lavagem" },
  { category: "roupa", activity: "Secagem" },
  { category: "roupa", activity: "Passagem a ferro" },
  { category: "roupa", activity: "Arrumação" },
  { category: "roupa", activity: "Mudança de roupa de cama e toalhas" },
  { category: "refeicoes", activity: "Preparação de refeições" },
  { category: "refeicoes", activity: "Gestão do frigorífico e despensa" },
  { category: "refeicoes", activity: "Pequenas compras" },
  { category: "refeicoes", activity: "Gestão de consumíveis" },
];
const CATEGORY_LABELS = {
  cuidados_pessoais: "Cuidados pessoais",
  manutencao_espaco: "Manutenção do espaço",
  roupa: "Roupa",
  refeicoes: "Refeições",
};
const FREQ_OPTS = ["Diária", "2x/semana", "3x/semana", "Semanal", "Quinzenal", "Mensal", "Conforme necessidade"];
const RESPONSIBLE_OPTS = ["Ajudante Familiar", "Família", "Ambos", "Outro"];
const AUTONOMY_OPTS = ["Autónomo", "Supervisão", "Assistência parcial", "Totalmente dependente"];

const inp = "w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/50";
const ta = inp + " min-h-[80px]";

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">{label}</div>
    {children}
  </label>
);

export default function TechnicalQualification() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("clinical");
  const [lead, setLead] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [di, setDi] = useState({ transport_regular: "", family_helper_requirements: "", requirements_level: "", notes: "" });
  const [diCompleted, setDiCompleted] = useState(false);
  const [guideOpen, setGuideOpen] = useState(true);
  const [activeBeneficiary, setActiveBeneficiary] = useState(1); // 1 or 2
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [appliedSuggestions, setAppliedSuggestions] = useState({});
  const currentUser = getUser();
  const isAdmin = currentUser?.role === "admin";
  const EMPTY_CLINICAL = { diagnoses:"", pathologies:"", mobility:"", autonomy:"", cognitive_state:"", fall_risk:"", continence:"", nutrition:"", medication:"", sleep:"", communication:"", equipment:"", home_environment:"", family_network:"", family_goals:"", identified_needs:"" };
  const [tech, setTech] = useState({
    clinical_state: { ...EMPTY_CLINICAL },
    clinical_state_2: null,
    care_plan: { objectives: [], interventions: [], activities: [], caregiver_profile: "", priorities: [], risks: [], recommendations: [] },
    technical_evaluation: "",
    family_feedback: "",
    close_probability: 50,
    objections: [],
    next_action: "",
    final_schedule: "",
    final_days: 5,
    final_hours_per_day: 8,
    caregiver_profile_final: "",
    final_start_date: "",
    tqa_name: "",
    office: "",
    custom_data: {},
    care_plan_custom_data: {},
  });
  const qtCfg = useFieldsConfig("qualificacao_tecnica");
  const { visible: V, label: L } = qtCfg;

  useEffect(() => {
    api.get(`/leads/${id}`).then((r) => {
      setLead(r.data);
      if (r.data.technical) {
        const t = r.data.technical;
        setTech((prev) => ({
          ...prev,
          ...t,
          clinical_state_2: t.clinical_state_2 || null,
          custom_data: t.custom_data || {},
          care_plan_custom_data: (t.care_plan || {}).custom_data || {},
        }));
      }
      const iv = r.data.diagnostic_interview || {};
      setDi({
        transport_regular: iv.transport_regular || "",
        family_helper_requirements: iv.family_helper_requirements || "",
        requirements_level: iv.requirements_level || "",
        notes: iv.notes || "",
      });
      setDiCompleted(!!iv.completed);
      if (r.data.diagnostic_analysis) {
        setAnalysis(r.data.diagnostic_analysis);
        // Rehydrate appliedSuggestions from persisted history
        const hist = r.data.diagnostic_analysis.applied_suggestions || [];
        const map = {};
        hist.forEach((h) => { map[`${h.beneficiary || 1}:${h.field}`] = true; });
        setAppliedSuggestions(map);
      }
    });
  }, [id]);

  const hasB2 = !!lead?.qualification?.has_second_beneficiary;
  const b1Name = lead?.qualification?.cared_person?.name || "Beneficiário 1";
  const b2Name = lead?.qualification?.cared_person_2?.name || "Beneficiário 2";

  // Ensure clinical_state_2 exists when a 2nd beneficiary is enabled
  useEffect(() => {
    if (hasB2 && tech.clinical_state_2 === null) {
      setTech((t) => ({ ...t, clinical_state_2: { ...EMPTY_CLINICAL } }));
    }
    // eslint-disable-next-line
  }, [hasB2]);

  const saveDi = async (patch = {}, opts = {}) => {
    try {
      const body = { ...di, ...patch };
      if (opts.completed !== undefined) body.completed = opts.completed;
      const { data } = await api.patch(`/leads/${id}/diagnostic-interview`, body);
      setDiCompleted(!!data.diagnostic_interview?.completed);
      if (opts.completed !== undefined) {
        toast.success(opts.completed ? "Entrevista Diagnóstico concluída — restantes tabs desbloqueadas" : "Entrevista reaberta");
      }
    } catch(e){ toast.error(e.response?.data?.detail || "Erro"); throw e; }
  };

  const setCl = (f, v) => setTech((t) => activeBeneficiary === 2
    ? { ...t, clinical_state_2: { ...(t.clinical_state_2 || EMPTY_CLINICAL), [f]: v } }
    : { ...t, clinical_state: { ...t.clinical_state, [f]: v } }
  );
  const currentClinical = activeBeneficiary === 2
    ? (tech.clinical_state_2 || EMPTY_CLINICAL)
    : tech.clinical_state;

  const setPlan = (f, v) => setTech((t) => ({ ...t, care_plan: { ...t.care_plan, [f]: v } }));
  const toggleObj = (o) => setTech((t) => ({ ...t, objections: t.objections.includes(o) ? t.objections.filter(x=>x!==o) : [...t.objections, o] }));

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      // Auto-guardar QT antes de analisar para que a IA veja os dados mais recentes
      const { care_plan_custom_data, ...rest } = tech;
      const payload = {
        ...rest,
        care_plan: { ...(rest.care_plan || {}), custom_data: care_plan_custom_data || {} },
      };
      await api.put(`/leads/${id}/technical`, payload);
      const { data } = await api.post(`/leads/${id}/diagnostic-analysis`);
      setAnalysis(data);
      setAnalysisOpen(true);
      toast.success("Análise IA concluída");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro na análise IA");
    } finally { setAnalyzing(false); }
  };

  const fillSample = (target) => {
    if (target === "clinical") {
      if (activeBeneficiary === 2) {
        setTech((t) => ({ ...t, clinical_state_2: { ...SAMPLE_CLINICAL_STATE_B2 } }));
      } else {
        setTech((t) => ({ ...t, clinical_state: { ...SAMPLE_CLINICAL_STATE_B1 } }));
      }
      if (!di.transport_regular) {
        const patch = { ...SAMPLE_DIAGNOSTIC_INTERVIEW };
        setDi(patch);
        saveDi(patch);
      }
      toast.success(`Exemplo aplicado (${activeBeneficiary === 2 ? b2Name : b1Name})`);
    } else if (target === "care_plan") {
      setTech((t) => ({ ...t, care_plan: { ...t.care_plan, ...SAMPLE_CARE_PLAN } }));
      toast.success("Exemplo aplicado no Plano de Cuidados");
    }
  };

  const applySuggestion = async (field, text) => {
    // Optimistic local update — append text to current beneficiary's clinical field
    const current = (currentClinical[field] || "").trim();
    const merged = current ? `${current}\n${text}` : text;
    setCl(field, merged);
    setAppliedSuggestions((prev) => ({ ...prev, [`${activeBeneficiary}:${field}`]: true }));
    try {
      const { data } = await api.post(`/leads/${id}/diagnostic-analysis/apply`, {
        items: [{ field, text, beneficiary: activeBeneficiary }],
      });
      if (data.applied_suggestions) {
        setAnalysis((a) => ({ ...(a || {}), applied_suggestions: data.applied_suggestions }));
      }
      toast.success(`Aplicado em "${field}" · ${activeBeneficiary === 2 ? b2Name : b1Name}`);
    } catch (e) {
      toast.error("Sugestão aplicada localmente, mas falhou persistir o histórico.");
    }
  };

  const applyAllSuggestions = async () => {
    if (!analysis?.suggestions) return;
    const entries = Object.entries(analysis.suggestions).filter(
      ([k]) => !appliedSuggestions[`${activeBeneficiary}:${k}`]
    );
    if (entries.length === 0) {
      toast.info("Todas as sugestões já foram aplicadas para este beneficiário.");
      return;
    }
    // Optimistic local updates in a single setState pass
    setTech((t) => {
      const cs = activeBeneficiary === 2 ? (t.clinical_state_2 || EMPTY_CLINICAL) : t.clinical_state;
      const updated = { ...cs };
      entries.forEach(([k, v]) => {
        const cur = (updated[k] || "").trim();
        updated[k] = cur ? `${cur}\n${v}` : v;
      });
      return activeBeneficiary === 2
        ? { ...t, clinical_state_2: updated }
        : { ...t, clinical_state: updated };
    });
    setAppliedSuggestions((prev) => {
      const next = { ...prev };
      entries.forEach(([k]) => { next[`${activeBeneficiary}:${k}`] = true; });
      return next;
    });
    try {
      const { data } = await api.post(`/leads/${id}/diagnostic-analysis/apply`, {
        items: entries.map(([field, text]) => ({ field, text, beneficiary: activeBeneficiary })),
      });
      if (data.applied_suggestions) {
        setAnalysis((a) => ({ ...(a || {}), applied_suggestions: data.applied_suggestions }));
      }
      toast.success(`${entries.length} sugestões aplicadas · ${activeBeneficiary === 2 ? b2Name : b1Name}`);
    } catch (e) {
      toast.error("Sugestões aplicadas localmente, mas falhou persistir o histórico.");
    }
  };

  const genCarePlan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${id}/care-plan`);
      setTech((t) => ({ ...t, care_plan: { ...t.care_plan, ...data } }));
      toast.success("Plano de cuidados gerado por IA");
    } catch (e) {
      // 25/02/2026 — Propaga a mensagem real do backend (400/500).
      // Antes o toast era genérico "Erro na geração" — o utilizador
      // ficava sem contexto do que falhou (JSON parse da IA, timeout,
      // rate-limit, etc.). Agora mostramos o detail do FastAPI.
      const detail = e?.response?.data?.detail;
      toast.error(detail ? `Erro na geração · ${detail}` : "Erro na geração — tenta outra vez em alguns segundos");
      // Log completo para debugging no browser console
      // eslint-disable-next-line no-console
      console.error("[care-plan] generation failed", { status: e?.response?.status, detail, error: e });
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { care_plan_custom_data, ...rest } = tech;
      const payload = {
        ...rest,
        care_plan: { ...(rest.care_plan || {}), custom_data: care_plan_custom_data || {} },
      };
      await api.put(`/leads/${id}/technical`, payload);
      toast.success("Reunião Técnica guardada");
      nav(`/leads/${id}`);
    }
    catch(e){ toast.error("Erro ao guardar"); } finally { setSaving(false); }
  };

  const addToList = (field) => setPlan(field, [...tech.care_plan[field], ""]);
  const updateList = (field, i, v) => setPlan(field, tech.care_plan[field].map((x,idx)=>idx===i?v:x));
  const removeFromList = (field, i) => setPlan(field, tech.care_plan[field].filter((_,idx)=>idx!==i));

  if (!lead) return <div className="p-10 text-muted-foreground">A carregar...</div>;

  return (
    <div className="p-8 lg:p-12 max-w-5xl mx-auto space-y-6" data-testid="qt-root">
      <Link to={`/leads/${id}`} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm"><ArrowLeft className="w-4 h-4" /> Voltar ao lead</Link>
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Fase 2 · Gestor de Cliente</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Reunião Técnica</h1>
        <p className="text-muted-foreground text-sm mt-2">Cliente: {lead.qualification?.contact?.name}</p>
      </div>

      <div className="flex gap-2 border-b border-border">
        {tabs.map((t) => {
          const locked = t.k !== "clinical" && !diCompleted;
          return (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={()=> !locked && setTab(t.k)}
              disabled={locked}
              title={locked ? "Conclui a Entrevista Diagnóstico para desbloquear" : ""}
              className={`px-5 py-3 text-sm transition-colors border-b-2 -mb-px ${tab===t.k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"} ${locked ? "opacity-40 cursor-not-allowed" : ""}`}>
              {locked ? "🔒 " : ""}{t.t}
            </button>
          );
        })}
      </div>

      {tab === "clinical" && (
        <div className="bg-card border border-border rounded-2xl p-8 space-y-6">
          {/* --- Perguntas obrigatórias da Entrevista Diagnóstico --- */}
          <div className="bg-muted/40 border border-primary/40 rounded-xl p-5 space-y-4" data-testid="di-mandatory">
            <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold">Perguntas obrigatórias da Entrevista Diagnóstico</div>

            <Field label="A zona de prestação tem transporte público regular? *">
              <div className="flex gap-2">
                {[["yes","Sim"],["no","Não"],["unknown","Não sei"]].map(([v,l])=>(
                  <button key={v} type="button" onClick={()=>{ setDi({...di,transport_regular:v}); saveDi({transport_regular:v}); }}
                    className={`text-xs px-3 py-1.5 rounded-full border ${di.transport_regular===v?"bg-primary border-primary text-foreground font-semibold":"bg-card border-border text-muted-foreground"}`}
                    data-testid={`di-transport-${v}`}>{l}</button>
                ))}
              </div>
            </Field>

            <Field label="Requisitos especiais quanto ao Ajudante Familiar">
              <textarea data-testid="di-family-helper" rows={2} className={ta} value={di.family_helper_requirements}
                onChange={(e)=>setDi({...di,family_helper_requirements:e.target.value})}
                onBlur={(e)=>saveDi({family_helper_requirements:e.target.value})}
                placeholder="Ex: experiência com Alzheimer, língua estrangeira, sexo específico, horário noturno…" />
            </Field>

            <Field label="Nível de exigência dos requisitos *">
              <div className="flex gap-2">
                {[["padrao","Padrão","#D1FAE5"],["elevado","Elevado","#FEF3C7"],["muito_elevado","Muito Elevado","#FFEDD5"]].map(([v,l,bg])=>(
                  <button key={v} type="button" onClick={()=>{ setDi({...di,requirements_level:v}); saveDi({requirements_level:v}); }}
                    className={`text-xs px-3 py-1.5 rounded-full border ${di.requirements_level===v?"font-semibold text-foreground border-primary":"bg-card border-border text-muted-foreground"}`}
                    style={di.requirements_level===v?{background:bg}:{}}
                    data-testid={`di-req-${v}`}>{l}</button>
                ))}
              </div>
            </Field>

            {di.requirements_level === "muito_elevado" && (
              <div className="rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] px-4 py-3 text-sm text-[hsl(var(--warning))] flex items-start gap-2" data-testid="di-select-banner">
                <span>💡</span>
                <div>
                  <b>Considere apresentar o plano Caring Select ao cliente.</b> Para requisitos muito exigentes, o plano premium responde melhor à necessidade da família (equipa dedicada, coordenador nomeado, suporte 24/7).
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              {diCompleted ? (
                <button onClick={()=>saveDi({}, {completed:false})} className="text-xs px-4 py-2 rounded-full border border-border text-muted-foreground hover:bg-muted" data-testid="di-reopen">Reabrir entrevista</button>
              ) : (
                <button onClick={()=>saveDi({}, {completed:true})} className="text-xs px-4 py-2 rounded-full bg-foreground hover:bg-slate-800 text-background font-semibold inline-flex items-center gap-2" data-testid="di-complete">
                  ✓ Concluir Entrevista e desbloquear
                </button>
              )}
            </div>
          </div>

          {/* --- Guia de preenchimento --- */}
          <div className="border border-primary/40 rounded-xl bg-gradient-to-br from-muted/40 to-muted/40 overflow-hidden" data-testid="di-guide">
            <button
              type="button"
              onClick={() => setGuideOpen((v) => !v)}
              className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-muted/40 transition-colors"
              data-testid="di-guide-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary/25 border border-primary/40 flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-foreground" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold">Guia rápido do Gestor</div>
                  <div className="text-sm text-foreground font-medium">O que escrever em cada campo da Entrevista Diagnóstico</div>
                </div>
              </div>
              <ChevronDown className={`w-5 h-5 text-foreground transition-transform ${guideOpen ? "rotate-180" : ""}`} />
            </button>

            {guideOpen && (
              <div className="px-5 pb-5 pt-1 space-y-4 text-sm text-[#374151]">
                <p className="text-[13px] text-muted-foreground leading-relaxed">
                  Preencha com <b>frases curtas e concretas</b>, na perspetiva do que a família <i>relatou</i> ou do que <i>observou</i> no domicílio.
                  Evite jargão clínico — o objetivo é orientar a Ajudante Familiar no terreno. Se ainda não tem dados, escreva “A confirmar” — nunca deixe em branco.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                  {/* Bloco 1 — Saúde clínica */}
                  <div className="space-y-2">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-foreground font-semibold">Saúde clínica</div>
                    <ul className="space-y-1.5 leading-snug">
                      <li><b>Diagnósticos.</b> Doenças confirmadas por médico (ex.: “Alzheimer estádio moderado, HTA controlada, diabetes tipo 2”).</li>
                      <li><b>Patologias.</b> Sintomas ativos e queixas recentes (dor lombar, tremor, obstipação, feridas).</li>
                      <li><b>Medicação.</b> Nº aproximado de fármacos, quem administra, se toma sozinho ou precisa supervisão. Sinalizar polimedicação (&gt;5 fármacos).</li>
                      <li><b>Continência.</b> Continente / incontinência urinária / fecal / mista. Uso de fralda ou algália, frequência das mudas.</li>
                      <li><b>Nutrição.</b> Come sozinho? Dieta especial (diabético, pastosa, triturada)? Perda de peso recente? Hidratação suficiente?</li>
                      <li><b>Sono.</b> Padrão (horas, sestas), agitação noturna, uso de indutor, risco de deambulação.</li>
                    </ul>
                  </div>

                  {/* Bloco 2 — Autonomia e mobilidade */}
                  <div className="space-y-2">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-foreground font-semibold">Autonomia & Mobilidade</div>
                    <ul className="space-y-1.5 leading-snug">
                      <li><b>Mobilidade.</b> Anda sozinho / com apoio / cadeira de rodas / acamado. Consegue subir escadas? Faz transferências?</li>
                      <li><b>Autonomia.</b> Nas AVDs (banho, vestir, comer, WC): autónomo, com supervisão, ajuda parcial ou totalmente dependente.</li>
                      <li><b>Risco de Quedas.</b> Nº de quedas nos últimos 6 meses, medo de cair, uso de auxiliares (bengala, andarilho), tapetes soltos.</li>
                      <li><b>Comunicação.</b> Fala fluente? Défice auditivo/visual? Compreende ordens simples? Prótese dentária/auditiva?</li>
                    </ul>
                  </div>

                  {/* Bloco 3 — Estado cognitivo & emocional */}
                  <div className="space-y-2">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-foreground font-semibold">Cognição & Emocional</div>
                    <ul className="space-y-1.5 leading-snug">
                      <li><b>Estado Cognitivo.</b> Orientado no tempo/espaço? Défices de memória? Confusão? Diagnóstico de demência ou declínio ligeiro?</li>
                      <li className="pl-3 border-l-2 border-primary/50 text-muted-foreground">
                        Sinalize alterações de comportamento: agitação, agressividade, apatia, isolamento — importantes para o perfil da Ajudante.
                      </li>
                    </ul>
                  </div>

                  {/* Bloco 4 — Ambiente e equipamentos */}
                  <div className="space-y-2">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-foreground font-semibold">Ambiente & Equipamentos</div>
                    <ul className="space-y-1.5 leading-snug">
                      <li><b>Ambiente habitacional.</b> Tipologia (T1/T2…), andar, elevador, escadas, WC adaptado, iluminação, riscos de queda.</li>
                      <li><b>Equipamentos existentes.</b> Cama articulada, colchão anti-escaras, cadeira de rodas, andarilho, elevador de transferência, oxigénio.</li>
                    </ul>
                  </div>

                  {/* Bloco 5 — Família e objetivos */}
                  <div className="space-y-2 md:col-span-2">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-foreground font-semibold">Rede familiar & Objetivos</div>
                    <ul className="space-y-1.5 leading-snug md:grid md:grid-cols-2 md:gap-x-6 md:space-y-0">
                      <li className="md:mb-1.5"><b>Rede Familiar.</b> Quem cuida hoje? Cuidador principal, disponibilidade, exaustão do cuidador informal, distância dos filhos.</li>
                      <li className="md:mb-1.5"><b>Objetivos da família.</b> O que esperam do serviço (alívio do cuidador, manter em casa, adiar lar, companhia, segurança à noite)?</li>
                      <li><b>Necessidades identificadas.</b> Súmula da equação: <i>“precisa de X horas de apoio para Y tarefas, começando em Z”</i>.</li>
                      <li className="text-muted-foreground"><b>Dica.</b> Se detetar requisitos exigentes (medicação complexa, demência avançada, 24/7), marque <b>Nível “Muito Elevado”</b> acima e apresente o plano <b>Caring Select</b>.</li>
                    </ul>
                  </div>
                </div>

                <div className="text-[11px] text-foreground/80 bg-card/50 border border-primary/30 rounded-lg px-3 py-2">
                  💬 <b>Precisa de mais detalhe?</b> Use o <b>Assistente do Manual</b> (botão flutuante em baixo à direita) — responde com base no <i>Manual do Cuidador</i> oficial da Caring.
                </div>
              </div>
            )}
          </div>

          {/* --- Restante estado clínico --- */}
          {/* Switcher entre beneficiários (se houver 2) + botões utilitários */}
          <div className="flex flex-wrap items-center gap-3 justify-between border-t border-border pt-5" data-testid="cl-toolbar">
            {hasB2 ? (
              <div className="inline-flex bg-muted border border-border rounded-full p-1" data-testid="beneficiary-switcher">
                <button type="button"
                  onClick={()=>setActiveBeneficiary(1)}
                  data-testid="beneficiary-tab-1"
                  className={`text-xs px-4 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${activeBeneficiary===1 ? "bg-card text-foreground shadow-sm font-semibold" : "text-muted-foreground"}`}>
                  <Users className="w-3.5 h-3.5" /> {b1Name}
                </button>
                <button type="button"
                  onClick={()=>setActiveBeneficiary(2)}
                  data-testid="beneficiary-tab-2"
                  className={`text-xs px-4 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${activeBeneficiary===2 ? "bg-card text-foreground shadow-sm font-semibold" : "text-muted-foreground"}`}>
                  <Users className="w-3.5 h-3.5" /> {b2Name}
                </button>
              </div>
            ) : <div />}
            {isAdmin && (
              <button type="button" onClick={()=>fillSample("clinical")} data-testid="admin-fill-clinical"
                className="text-[11px] px-3 py-1.5 rounded-full border border-dashed border-primary text-foreground hover:bg-muted/40 inline-flex items-center gap-1.5">
                <Wand2 className="w-3 h-3" /> Admin · Preencher com exemplo
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {Object.entries({
              diagnoses:"Diagnósticos", pathologies:"Patologias", mobility:"Mobilidade",
              autonomy:"Autonomia", cognitive_state:"Estado Cognitivo", fall_risk:"Risco de Quedas",
              continence:"Continência", nutrition:"Nutrição", medication:"Medicação",
              sleep:"Sono", communication:"Comunicação", equipment:"Equipamentos existentes",
              home_environment:"Ambiente habitacional", family_network:"Rede Familiar",
              family_goals:"Objetivos da família", identified_needs:"Necessidades identificadas",
            }).map(([k, defLabel]) => {
              const key = `clinical_state.${k}`;
              if (!V(key)) return null;
              return (
                <Field key={k} label={L(key, defLabel)}>
                  <textarea data-testid={`cl-${k}`} className={ta} rows={2} value={currentClinical[k] || ""} onChange={(e)=>setCl(k, e.target.value)} />
                </Field>
              );
            })}
          </div>
          <CustomFieldsSection
            screen="qualificacao_tecnica"
            value={tech.custom_data}
            onChange={(v)=> setTech((t)=>({ ...t, custom_data: v }))}
          />

          {/* --- Análise IA da Entrevista Diagnóstico --- */}
          <div className="border-t border-border pt-6 flex flex-wrap items-center justify-between gap-3" data-testid="ai-analysis-bar">
            <div className="max-w-lg">
              <div className="text-sm font-medium text-foreground flex items-center gap-2"><Brain className="w-4 h-4 text-foreground" /> Análise IA do diagnóstico</div>
              <p className="text-[12px] text-muted-foreground mt-1">A IA revê o que preencheu, identifica lacunas, riscos e sugere correções — baseada no Manual do Cuidador.</p>
            </div>
            <div className="flex items-center gap-2">
              {analysis && (
                <button type="button" onClick={()=>setAnalysisOpen(true)} data-testid="analysis-view-btn"
                  className="text-[11px] px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:bg-muted">
                  Ver última análise
                </button>
              )}
              <button type="button" onClick={runAnalysis} disabled={analyzing} data-testid="analysis-run-btn"
                className="text-xs px-4 py-2 rounded-full bg-foreground hover:bg-foreground/90 text-background font-semibold inline-flex items-center gap-2 disabled:opacity-60">
                {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Brain className="w-3.5 h-3.5" />}
                {analyzing ? "A analisar…" : "Analisar com IA"}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === "care_plan" && (
        <div className="bg-card border border-border rounded-2xl p-8 space-y-6">
          <div className="flex justify-end gap-2">
            {isAdmin && (
              <button type="button" onClick={()=>fillSample("care_plan")} data-testid="admin-fill-careplan"
                className="text-[11px] px-3 py-1.5 rounded-full border border-dashed border-primary text-foreground hover:bg-muted/40 inline-flex items-center gap-1.5">
                <Wand2 className="w-3 h-3" /> Admin · Preencher com exemplo
              </button>
            )}
            <button data-testid="ai-care-plan" onClick={genCarePlan} disabled={busy} className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 text-foreground hover:bg-primary/20 rounded-md px-3 py-1.5 text-xs disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Gerar com IA
            </button>
          </div>

          {["objectives","priorities","risks","recommendations"].map((field) => (
            <Field key={field} label={{objectives:"Objetivos",priorities:"Prioridades",risks:"Riscos",recommendations:"Recomendações"}[field]}>
              <div className="space-y-2">
                {tech.care_plan[field].map((item, i) => (
                  <div key={i} className="flex gap-2">
                    <input data-testid={`cp-${field}-${i}`} className={inp} value={item} onChange={(e)=>updateList(field, i, e.target.value)} />
                    <button onClick={()=>removeFromList(field, i)} className="text-muted-foreground/70 hover:text-rose-400 p-2"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <button data-testid={`cp-add-${field}`} onClick={()=>addToList(field)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Adicionar</button>
              </div>
            </Field>
          ))}

          <Field label="Perfil recomendado do cuidador">
            <textarea data-testid="cp-caregiver" className={ta} rows={3} value={tech.care_plan.caregiver_profile} onChange={(e)=>setPlan("caregiver_profile", e.target.value)} />
          </Field>

          <Field label="Intervenções">
            <div className="space-y-3">
              {tech.care_plan.interventions.map((it, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-start bg-muted/70 rounded-lg p-3 border border-border">
                  <input placeholder="Objetivo" className={inp} value={it.objective||""} onChange={(e)=>setPlan("interventions", tech.care_plan.interventions.map((x,idx)=>idx===i?{...x,objective:e.target.value}:x))} />
                  <input placeholder="Intervenção" className={inp} value={it.intervention||""} onChange={(e)=>setPlan("interventions", tech.care_plan.interventions.map((x,idx)=>idx===i?{...x,intervention:e.target.value}:x))} />
                  <div className="flex gap-2">
                    <input placeholder="Frequência" className={inp} value={it.frequency||""} onChange={(e)=>setPlan("interventions", tech.care_plan.interventions.map((x,idx)=>idx===i?{...x,frequency:e.target.value}:x))} />
                    <button onClick={()=>setPlan("interventions", tech.care_plan.interventions.filter((_,idx)=>idx!==i))} className="text-muted-foreground/70 hover:text-rose-400 p-2"><X className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              <button data-testid="cp-add-intervention" onClick={()=>setPlan("interventions", [...tech.care_plan.interventions, {objective:"",intervention:"",frequency:""}])} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Adicionar intervenção</button>
            </div>
          </Field>

          <CustomFieldsSection
            screen="plano_cuidados"
            value={tech.care_plan_custom_data}
            onChange={(v)=> setTech((t)=>({ ...t, care_plan_custom_data: v }))}
          />
        </div>
      )}

      {tab === "activities" && (
        <div className="bg-card border border-border rounded-2xl p-8 space-y-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">Configure a frequência, responsável e nível de autonomia por atividade. As atividades marcadas serão incluídas na proposta final.</div>
            {(tech.care_plan.activities||[]).length === 0 && (
              <button data-testid="activities-load-template" onClick={()=>setPlan("activities", ACTIVITY_TEMPLATE.map(a=>({...a, frequency:"Diária", responsible:"Ajudante Familiar", autonomy_level:"Assistência parcial", notes:"", included: true})))}
                className="text-xs bg-primary/10 border border-primary/30 text-foreground hover:bg-primary/20 rounded-full px-3 py-1.5">
                Carregar template Caring
              </button>
            )}
          </div>
          {Object.entries(CATEGORY_LABELS).map(([cat, label]) => {
            const items = (tech.care_plan.activities || []).map((a, i) => ({ ...a, __i: i })).filter((a) => a.category === cat);
            const gridTpl = hasB2
              ? "grid-cols-[24px_1.5fr_1fr_1fr_1fr_1fr_100px_28px]"
              : "grid-cols-[24px_1.5fr_1fr_1fr_1fr_1fr_28px]";
            return (
              <div key={cat} className="border border-border rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-ui text-lg text-foreground">{label}</h4>
                  <button onClick={()=>setPlan("activities", [...(tech.care_plan.activities||[]), { category:cat, activity:"", frequency:"Diária", responsible:"Ajudante Familiar", autonomy_level:"Assistência parcial", notes:"", included:true, beneficiary: hasB2 ? "both" : null }])} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Adicionar</button>
                </div>
                <div className="space-y-2">
                  {items.length === 0 && <div className="text-xs text-muted-foreground/70">Sem atividades nesta categoria.</div>}
                  {items.map((a) => (
                    <div key={a.__i} className={`grid ${gridTpl} gap-2 items-center bg-muted/70 border border-border rounded-lg p-2.5`}>
                      <input type="checkbox" checked={a.included} onChange={(e)=>setPlan("activities", tech.care_plan.activities.map((x, idx) => idx===a.__i ? {...x, included: e.target.checked} : x))} className="accent-primary" />
                      <input placeholder="Atividade" className={inp} value={a.activity||""} onChange={(e)=>setPlan("activities", tech.care_plan.activities.map((x, idx) => idx===a.__i ? {...x, activity: e.target.value} : x))} />
                      <select className={inp} value={a.frequency||""} onChange={(e)=>setPlan("activities", tech.care_plan.activities.map((x, idx) => idx===a.__i ? {...x, frequency: e.target.value} : x))}>
                        <option value="">Frequência</option>{FREQ_OPTS.map(f=><option key={f}>{f}</option>)}
                      </select>
                      <select className={inp} value={a.responsible||""} onChange={(e)=>setPlan("activities", tech.care_plan.activities.map((x, idx) => idx===a.__i ? {...x, responsible: e.target.value} : x))}>
                        <option value="">Responsável</option>{RESPONSIBLE_OPTS.map(r=><option key={r}>{r}</option>)}
                      </select>
                      <select className={inp} value={a.autonomy_level||""} onChange={(e)=>setPlan("activities", tech.care_plan.activities.map((x, idx) => idx===a.__i ? {...x, autonomy_level: e.target.value} : x))}>
                        <option value="">Autonomia</option>{AUTONOMY_OPTS.map(o=><option key={o}>{o}</option>)}
                      </select>
                      <input placeholder="Notas" className={inp} value={a.notes||""} onChange={(e)=>setPlan("activities", tech.care_plan.activities.map((x, idx) => idx===a.__i ? {...x, notes: e.target.value} : x))} />
                      {hasB2 && (
                        <select
                          data-testid={`act-beneficiary-${a.__i}`}
                          title="Beneficiário"
                          className={inp}
                          value={a.beneficiary || "both"}
                          onChange={(e)=>setPlan("activities", tech.care_plan.activities.map((x, idx) => idx===a.__i ? {...x, beneficiary: e.target.value} : x))}>
                          <option value="both">Ambos</option>
                          <option value="1">{b1Name}</option>
                          <option value="2">{b2Name}</option>
                        </select>
                      )}
                      <button onClick={()=>setPlan("activities", tech.care_plan.activities.filter((_,idx)=>idx!==a.__i))} className="text-muted-foreground/70 hover:text-rose-400"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "final" && (
        <div className="bg-card border border-border rounded-2xl p-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Field label="Horário"><input data-testid="final-schedule" className={inp} placeholder="08h-18h" value={tech.final_schedule||""} onChange={(e)=>setTech({...tech, final_schedule:e.target.value})} /></Field>
            <Field label="Dias por semana"><input data-testid="final-days" type="number" min={1} max={7} className={inp} value={tech.final_days||""} onChange={(e)=>setTech({...tech, final_days:parseInt(e.target.value)||null})} /></Field>
            <Field label="Horas por dia"><input data-testid="final-hours" type="number" step="0.5" className={inp} value={tech.final_hours_per_day||""} onChange={(e)=>setTech({...tech, final_hours_per_day:parseFloat(e.target.value)||null})} /></Field>
            <Field label="Data de início"><input data-testid="final-start" type="date" className={inp} value={tech.final_start_date||""} onChange={(e)=>setTech({...tech, final_start_date:e.target.value})} /></Field>
            <Field label="Direção Técnica"><input data-testid="final-tqa" className={inp} placeholder="Nome da Direção Técnica" value={tech.tqa_name||""} onChange={(e)=>setTech({...tech, tqa_name:e.target.value})} /></Field>
            <Field label="Escritório / Distrito"><input data-testid="final-office" className={inp} placeholder="Ex: Lisboa" value={tech.office||""} onChange={(e)=>setTech({...tech, office:e.target.value})} /></Field>
          </div>
          <Field label="Perfil final da Ajudante Familiar">
            <textarea data-testid="final-caregiver" className={ta} rows={3} value={tech.caregiver_profile_final||""} onChange={(e)=>setTech({...tech, caregiver_profile_final:e.target.value})} />
          </Field>
        </div>
      )}

      {tab === "evaluation" && (
        <div className="bg-card border border-border rounded-2xl p-8 space-y-6">
          <Field label="Avaliação Técnica"><textarea data-testid="ev-technical" className={ta} rows={4} value={tech.technical_evaluation} onChange={(e)=>setTech({...tech, technical_evaluation:e.target.value})} /></Field>
          <Field label="Feedback da Família"><textarea data-testid="ev-family" className={ta} rows={4} value={tech.family_feedback} onChange={(e)=>setTech({...tech, family_feedback:e.target.value})} /></Field>
          <Field label={`Probabilidade de Fecho: ${tech.close_probability}%`}>
            <input data-testid="ev-prob" type="range" min="0" max="100" value={tech.close_probability} onChange={(e)=>setTech({...tech, close_probability:parseInt(e.target.value)})} className="w-full accent-primary" />
          </Field>
          <Field label="Objeções">
            <div className="flex flex-wrap gap-2">
              {OBJECTIONS.map((o) => (
                <button key={o} data-testid={`ev-obj-${o}`} onClick={()=>toggleObj(o)}
                  className={`px-3 py-1.5 rounded-full text-xs border ${tech.objections.includes(o) ? "bg-primary text-foreground border-primary" : "bg-transparent text-muted-foreground border-border hover:border-primary/50"}`}>{o}</button>
              ))}
            </div>
          </Field>
          <Field label="Próxima ação">
            <select data-testid="ev-next" className={inp} value={tech.next_action} onChange={(e)=>setTech({...tech, next_action:e.target.value})}>
              <option value="">—</option>
              {NEXT_ACTIONS.map(n=><option key={n.value} value={n.value}>{n.label}</option>)}
            </select>
          </Field>
        </div>
      )}

      <div className="flex justify-end">
        <button data-testid="qt-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-foreground hover:bg-slate-800 text-background font-semibold rounded-full px-6 py-3 text-sm">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar Qualificação
        </button>
      </div>

      {/* --- Dialog: Análise IA da Entrevista Diagnóstico --- */}
      <Dialog open={analysisOpen} onOpenChange={setAnalysisOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="analysis-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-2xl">
              <Brain className="w-6 h-6 text-foreground" />
              Análise IA do Diagnóstico
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Revisão automática do estado clínico, entrevista e contexto — apoiada no Manual do Cuidador.
            </DialogDescription>
          </DialogHeader>

          {!analysis ? (
            <div className="py-10 text-center text-muted-foreground">Sem análise disponível.</div>
          ) : (
            <div className="space-y-5 text-sm text-foreground" data-testid="analysis-content">
              {/* Overall + score */}
              <div className="grid grid-cols-1 md:grid-cols-[1fr_140px] gap-4 items-start">
                <div className="bg-muted/40 border border-primary/40 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold mb-1">Avaliação global</div>
                  <p className="leading-relaxed">{analysis.overall_assessment || "—"}</p>
                </div>
                {typeof analysis.completeness_score === "number" && (
                  <div className="text-center bg-card border border-border rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Completude</div>
                    <div className={`font-display text-4xl mt-1 ${analysis.completeness_score >= 70 ? "text-[#059669]" : analysis.completeness_score >= 40 ? "text-foreground" : "text-[hsl(var(--danger))]"}`}>{analysis.completeness_score}%</div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      <TrendingUp className="w-3 h-3 inline mr-0.5" /> score do diagnóstico
                    </div>
                  </div>
                )}
              </div>

              {/* Recommended plan */}
              {analysis.recommended_plan && (
                <div className="bg-[hsl(var(--success-bg))] border border-[#059669]/30 rounded-lg p-3 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#059669] mt-0.5" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-[#059669] font-semibold">Plano recomendado</div>
                    <div className="mt-0.5">{analysis.recommended_plan}</div>
                  </div>
                </div>
              )}

              {/* Gaps */}
              {analysis.gaps?.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-foreground font-semibold mb-2">Lacunas críticas ({analysis.gaps.length})</div>
                  <div className="space-y-1.5">
                    {analysis.gaps.map((g, i) => (
                      <div key={i} className="bg-card border border-border rounded-lg px-3 py-2 flex items-start gap-2" data-testid={`analysis-gap-${i}`}>
                        <span className={`text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5 rounded-full ${g.severity === "high" ? "bg-[hsl(var(--warning-bg))] text-[hsl(var(--danger))]" : g.severity === "medium" ? "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))]" : "bg-muted text-muted-foreground"}`}>{g.severity || "med"}</span>
                        <div className="flex-1">
                          <div className="font-semibold text-foreground">{g.label || g.field}</div>
                          <div className="text-[13px] text-muted-foreground">{g.issue}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Risks */}
              {analysis.risks?.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--danger))] font-semibold mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Alertas de risco ({analysis.risks.length})</div>
                  <div className="space-y-1.5">
                    {analysis.risks.map((r, i) => (
                      <div key={i} className="bg-[hsl(var(--warning-bg))]/40 border border-[#FFEDD5] rounded-lg px-3 py-2" data-testid={`analysis-risk-${i}`}>
                        <div className="font-semibold text-foreground">{r.title}</div>
                        <div className="text-[13px] text-muted-foreground mt-0.5">{r.detail}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Suggestions per field */}
              {analysis.suggestions && Object.keys(analysis.suggestions).length > 0 && (() => {
                const notApplied = Object.entries(analysis.suggestions).filter(
                  ([k]) => !appliedSuggestions[`${activeBeneficiary}:${k}`]
                );
                return (
                <div>
                  <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-foreground font-semibold">
                      Sugestões por campo · <span className="normal-case tracking-normal text-muted-foreground">aplique em {activeBeneficiary === 2 ? b2Name : b1Name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={applyAllSuggestions}
                      disabled={notApplied.length === 0}
                      data-testid="apply-all-suggestions"
                      className={`text-[10px] uppercase tracking-[0.14em] font-semibold px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${
                        notApplied.length === 0
                          ? "bg-muted text-muted-foreground/70 cursor-not-allowed"
                          : "bg-primary hover:bg-[#6d5030] text-primary-foreground"
                      }`}>
                      {notApplied.length === 0 ? "Todas aplicadas ✓" : `Aplicar todos (${notApplied.length})`}
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {Object.entries(analysis.suggestions).map(([k, v]) => {
                      const key = `${activeBeneficiary}:${k}`;
                      const applied = !!appliedSuggestions[key];
                      return (
                        <div key={k} className="bg-muted/40 border border-primary/30 rounded-lg px-3 py-2 flex items-start gap-3" data-testid={`analysis-suggestion-${k}`}>
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.14em] text-foreground font-semibold">{k}</div>
                            <div className="text-[13px] mt-0.5">{v}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => applySuggestion(k, v)}
                            disabled={applied}
                            data-testid={`apply-suggestion-${k}`}
                            className={`text-[10px] uppercase tracking-[0.14em] font-semibold px-3 py-1.5 rounded-full whitespace-nowrap self-center transition-colors ${
                              applied
                                ? "bg-[hsl(var(--success-bg))] text-[#059669] border border-[#059669]/30 cursor-default"
                                : "bg-foreground hover:bg-foreground/90 text-background"
                            }`}>
                            {applied ? "Aplicada ✓" : "Aplicar"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-2">
                    💡 As sugestões são <b>anexadas</b> ao texto existente do campo (não substituem). Reveja e edite antes de guardar.
                  </div>
                </div>
                );
              })()}

              {/* Applied suggestions history */}
              {analysis.applied_suggestions?.length > 0 && (
                <div data-testid="analysis-history">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">
                    Histórico de sugestões aplicadas ({analysis.applied_suggestions.length})
                  </div>
                  <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                    {analysis.applied_suggestions.slice().reverse().map((h, i) => (
                      <div key={i} className="bg-card border border-border rounded-md px-3 py-1.5 text-[11px] text-muted-foreground flex items-center gap-2">
                        <span className="text-[9px] uppercase tracking-[0.14em] font-semibold px-1.5 py-0.5 rounded bg-muted text-foreground">
                          B{h.beneficiary || 1}
                        </span>
                        <span className="text-foreground font-semibold min-w-0 truncate">{h.field}</span>
                        <span className="text-muted-foreground/70 ml-auto flex-shrink-0">
                          {h.applied_at ? new Date(h.applied_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}
                        </span>
                        {h.applied_by && (
                          <span className="text-muted-foreground/70 flex-shrink-0 hidden sm:inline">· {h.applied_by.split("@")[0]}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Caregiver profile */}
              {analysis.caregiver_profile && (
                <div className="bg-card border border-border rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1">Perfil de Ajudante recomendado</div>
                  <p className="text-[13px] leading-relaxed">{analysis.caregiver_profile}</p>
                </div>
              )}

              {analysis.generated_at && (
                <div className="text-[10px] text-muted-foreground/70 text-right">Gerada em {new Date(analysis.generated_at).toLocaleString("pt-PT")}</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
